<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="EmploymentStatus" class="form" data-test-id="employment-status-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.EMPLOYMENT_STATUS.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="employment-status-header">
              <span class="headerlight">
                What's your <strong>employment status?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your employment status...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isEmploymentStatusComplete && formHidden && !isSyncing"
          :submitted="isEmploymentStatusComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="employmentStatus"
                :error-messages="employmentStatusErrors"
                hide-details="auto"
                @change="$v.employmentStatus.$touch()"
                data-test-id="employment-status-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="EMPLOYMENT_STATUSES.EMPLOYED"
                  :value="EMPLOYMENT_STATUSES.EMPLOYED"
                  data-test-id="employed-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  :label="EMPLOYMENT_STATUSES.SELF_EMPLOYED"
                  :value="EMPLOYMENT_STATUSES.SELF_EMPLOYED"
                  data-test-id="self-employed-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="EMPLOYMENT_STATUSES.STUDENT"
                  :value="EMPLOYMENT_STATUSES.STUDENT"
                  data-test-id="student-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="EMPLOYMENT_STATUSES.HOMEMAKER"
                  :value="EMPLOYMENT_STATUSES.HOMEMAKER"
                  data-test-id="homemaker-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="EMPLOYMENT_STATUSES.RETIRED"
                  :value="EMPLOYMENT_STATUSES.RETIRED"
                  data-test-id="retired-radio"
                ></v-radio>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  :label="EMPLOYMENT_STATUSES.NOT_EMPLOYED"
                  :value="EMPLOYMENT_STATUSES.NOT_EMPLOYED"
                  data-test-id="not-employed-radio"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="employment-status-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS, EMPLOYMENT_STATUSES } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'EmploymentStatus',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    employmentStatus: null,
    MENUS: MENUS,
    EMPLOYMENT_STATUSES: EMPLOYMENT_STATUSES,
  }),

  validations() {
    return {
      employmentStatus: {
        required,
      },
      validationGroup: ['employmentStatus'],
    };
  },

  computed: {
    // Is employment status complete
    isEmploymentStatusComplete() {
      return this.isPageComplete(MENUS.CLIENT.EMPLOYMENT_STATUS.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.EMPLOYMENT_STATUS.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    employmentStatusErrors() {
      const errors = [];
      if (!this.$v.employmentStatus.$dirty) return errors;
      !this.$v.employmentStatus.required &&
        errors.push('A selection is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      let updatedPages = this.pages;
      const isEmployed = this.employmentStatus === this.EMPLOYMENT_STATUSES.EMPLOYED ||
        this.employmentStatus === this.EMPLOYMENT_STATUSES.SELF_EMPLOYED;
      
      if (isEmployed && !this.interviewPages.getWithEmploymentPages()) {
        this.interviewPages.addEmploymentPages();
        updatedPages = this.interviewPages.getInterviewPages();
      }
      if (!isEmployed && this.interviewPages.getWithEmploymentPages()) {
        this.interviewPages.removeEmploymentPages();
        updatedPages = this.interviewPages.getInterviewPages();
      }

      let to = route || updatedPages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        status: this.employmentStatus,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(updatedPages[this.pageIndex]);

      this.completeStep(updatedPages[this.pageIndex], data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isEmploymentStatusComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isEmploymentStatusComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.EMPLOYMENT_STATUS.id);
    // Initialize form data for current interview
    this.formHidden = this.isEmploymentStatusComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If employment status is complete, hide the form
      if (this.isEmploymentStatusComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
